import { SelectItemModel } from '@cp/ds/src/components/select/model';
import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { addMilliseconds, format, isTomorrow, subMilliseconds } from 'date-fns';
import { DateFormat } from '@common/const/date';
import { FormFieldName, FormModel, FormSubmitTrigger } from '../form';
import { MIN_RANGE_BETWEEN_START_AND_END_IN_MS, TIME_FIELD_LIST_STEP_IN_MS } from '../const';
import { getStartDateFromValue } from '../utils/getStartDateFromValue';

const generateItems = ({ startDate }: { startDate?: Date }) => {
  const items: SelectItemModel<string>[] = [];

  if (!startDate) {
    return items;
  }

  const baseDate = startDate && addMilliseconds(startDate, MIN_RANGE_BETWEEN_START_AND_END_IN_MS);
  let listItemDate = baseDate;

  while (!isTomorrow(subMilliseconds(listItemDate, 1))) {
    const time = format(listItemDate, DateFormat.Time);
    items.push({ label: time, value: time });

    listItemDate = addMilliseconds(listItemDate, TIME_FIELD_LIST_STEP_IN_MS);
  }

  return items;
};

export const useEnd = ({ form, submit }: { form: UseFormReturn<FormModel>; submit: FormSubmitTrigger }) => {
  // Reason to use 'useWatch' and 'getValues' together:
  // https://github.com/react-hook-form/react-hook-form/issues/2738
  useWatch<FormModel, FormFieldName.Start>({ name: FormFieldName.Start });
  const startValue = form.getValues(FormFieldName.Start);

  const items = React.useMemo<SelectItemModel<string>[]>(() => {
    return generateItems({ startDate: getStartDateFromValue(startValue) });
  }, [startValue]);

  /**
   * Checks to keep form in a consistent state and make supposed choise for user
   */
  React.useEffect(() => {
    const end = form.getValues(FormFieldName.End);

    if (startValue && !end && items.length) {
      // Make supposed choise for user
      form.setValue(FormFieldName.End, items[0].value);
      submit();
    }

    if (end && (!items.find((item) => item.value === end) || !startValue)) {
      // Reset end value and submit new values
      form.setValue(FormFieldName.End, null);
      submit();
    }
  }, [form, items, startValue, submit]);

  return { items, disabled: !items.length };
};
